import type { SaunaPhase } from '@jums-dashboard/shared';
import { useEffect, useMemo, useRef, useState } from 'react';

import Badge from '@/components/Badge/Badge';
import Button from '@/components/Button/Button';
import Widget from '@/components/Widget/Widget';
import WidgetSettingsModal from '@/features/dashboard/components/WidgetSettingsModal';
import { useAbortSauna, useAcknowledgeSaunaReady, useFinishSauna, useStartSauna } from '@/features/homeAutomation/hooks/useSaunaMutations';
import { cn } from '@/lib/cn';

import SaunaDetailModal from './SaunaDetailModal';
import { playReadyChime } from './playReadyChime';
import useSaunaWidgetConfig from './useSaunaWidgetConfig';
import useSaunaWidgetData from './useSaunaWidgetData';

const PHASE_TONE: Record<SaunaPhase, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  idle: 'neutral',
  warming: 'info',
  ready: 'danger',
  inUse: 'success',
  cooldown: 'warning',
  stale: 'neutral',
};

const PHASE_LABEL: Record<SaunaPhase, string> = {
  idle: 'idle',
  warming: 'warming',
  ready: 'ready',
  inUse: 'in use',
  cooldown: 'cooldown',
  stale: 'stale',
};

function fmtTempBig(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toFixed(1);
}

/**
 * Ticks every second to display a live countdown to `iso`. Re-syncs naturally
 * when the parent re-renders with a new ETA (backend pushed an updated value).
 */
function ReadyCountdown({ iso }: { iso: string }) {
  const targetMs = useMemo(() => Date.parse(iso), [iso]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!Number.isFinite(targetMs)) return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [targetMs]);

  if (!Number.isFinite(targetMs)) return <>—</>;
  const remainingMs = targetMs - nowMs;
  if (remainingMs <= 0) return <>any moment</>;

  const totalSeconds = Math.floor(remainingMs / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return <span className="tabular-nums">{h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`}</span>;
}

function fmtSinceMin(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const min = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  return `${h}h ago`;
}

export default function SaunaWidget() {
  const config = useSaunaWidgetConfig();
  const homeId = config.homeId ?? undefined;
  const sauna = useSaunaWidgetData(config);

  const start = useStartSauna();
  const finish = useFinishSauna();
  const ack = useAcknowledgeSaunaReady();
  const abort = useAbortSauna();

  const [detailOpen, setDetailOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasHome = !!homeId;
  const cs = sauna.data?.currentState ?? null;
  const phase: SaunaPhase = cs?.phase ?? 'idle';
  const hasSensor = !!sauna.data?.sensorId;

  const previousPhaseRef = useRef<SaunaPhase | null>(null);
  useEffect(() => {
    const prev = previousPhaseRef.current;
    if (prev && prev !== 'ready' && phase === 'ready' && config.soundOnReady) {
      playReadyChime(config.chimeSound);
    }
    previousPhaseRef.current = phase;
  }, [phase, config.soundOnReady, config.chimeSound]);

  const needsSetup = !hasHome || (!sauna.isPending && !sauna.isError && (!sauna.data?.exists || !hasSensor));
  const status = sauna.isPending ? 'loading' : sauna.isError ? 'error' : 'loaded';
  const setupMessage = !hasHome ? 'Pick a home in widget settings.' : 'Pick a sauna sensor in Home settings (user menu → Home).';

  const busy = start.isPending || finish.isPending || ack.isPending || abort.isPending;
  const canStart = hasSensor && (phase === 'idle' || phase === 'cooldown' || phase === 'stale');
  const canFinish = phase === 'ready' || phase === 'inUse';
  const canAck = phase === 'ready';
  const canAbort = phase === 'warming' || phase === 'ready';

  return (
    <>
      <Widget
        status={status}
        errorMessage={sauna.error instanceof Error ? sauna.error.message : 'Failed to load sauna.'}
        loadingMessage="Loading sauna…"
        needsSetup={needsSetup}
        setupMessage={setupMessage}
        onConfigure={() => setSettingsOpen(true)}
        onOpenDetail={hasHome && hasSensor ? () => setDetailOpen(true) : undefined}
      >
        <SaunaBody
          phase={phase}
          cs={cs}
          targetC={
            phase === 'idle' || phase === 'stale'
              ? (sauna.data?.config?.targetTemperatureC ?? cs?.targetTemperatureC ?? null)
              : (cs?.targetTemperatureC ?? sauna.data?.config?.targetTemperatureC ?? null)
          }
          busy={busy}
          canStart={canStart}
          canFinish={canFinish}
          canAck={canAck}
          canAbort={canAbort}
          onStart={() => homeId && start.mutate({ homeId })}
          onFinish={() => homeId && finish.mutate({ homeId })}
          onAck={() => homeId && ack.mutate({ homeId })}
          onAbort={() => homeId && abort.mutate({ homeId })}
        />
      </Widget>

      {hasHome && (
        <SaunaDetailModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          homeId={homeId!}
          historyDays={config.historyDays}
          lastUpdated={sauna.dataUpdatedAt ? new Date(sauna.dataUpdatedAt) : null}
          onRefresh={() => sauna.refetch()}
        />
      )}
      <WidgetSettingsModal widgetId="sauna" open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}

interface SaunaBodyProps {
  phase: SaunaPhase;
  cs: NonNullable<ReturnType<typeof useSaunaWidgetData>['data']>['currentState'];
  targetC: number | null;
  busy: boolean;
  canStart: boolean;
  canFinish: boolean;
  canAck: boolean;
  canAbort: boolean;
  onStart: () => void;
  onFinish: () => void;
  onAck: () => void;
  onAbort: () => void;
}

function SaunaBody({ phase, cs, targetC, busy, canStart, canFinish, canAck, canAbort, onStart, onFinish, onAck, onAbort }: SaunaBodyProps) {
  const currentTemp = cs?.currentTemperatureC ?? null;
  const startTemp = cs?.startTemperatureC ?? null;
  const progress = useMemo(() => {
    if (!cs || currentTemp == null || targetC == null || startTemp == null) return null;
    if (targetC <= startTemp) return null;
    const p = (currentTemp - startTemp) / (targetC - startTemp);
    return Math.min(1, Math.max(0, p));
  }, [cs, currentTemp, targetC, startTemp]);

  // Phase-driven background accent
  const bgAccent =
    phase === 'ready'
      ? 'bg-(--accent-danger)/15'
      : phase === 'warming'
        ? 'bg-(--accent-warning)/12'
        : phase === 'inUse'
          ? 'bg-(--accent-success)/8'
          : phase === 'cooldown'
            ? 'bg-(--accent-info)/10'
            : 'bg-transparent';

  return (
    <div className={cn('relative -m-2 flex h-full flex-col justify-center gap-2 rounded-md p-2 transition-colors', bgAccent)}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pr-6">
        <span className="text-[11px] font-semibold tracking-[0.14em] text-(--text-tertiary) uppercase">Sauna</span>
        <div
          className={cn(
            'flex items-baseline gap-2',
            phase === 'ready' && 'text-(--accent-danger) motion-safe:animate-[readyPulse_1.6s_ease-in-out_infinite]',
          )}
        >
          <span className="font-display text-4xl leading-none font-semibold text-(--text-primary) tabular-nums">
            {fmtTempBig(currentTemp)}°
          </span>
          {targetC != null && phase !== 'ready' && <span className="text-sm text-(--text-tertiary)">→ {Math.round(targetC)}°</span>}
        </div>
        <Badge tone={PHASE_TONE[phase]}>{PHASE_LABEL[phase]}</Badge>
        <span className="text-sm text-(--text-muted)">
          {phase === 'warming' && cs?.estimatedReadyAt ? (
            <>
              ready in <ReadyCountdown iso={cs.estimatedReadyAt} />
            </>
          ) : phase === 'ready' ? (
            <span className="font-semibold tracking-wide text-(--accent-danger) uppercase">Ready</span>
          ) : phase === 'inUse' && cs?.lastTransitionAt ? (
            <>started {fmtSinceMin(cs.lastTransitionAt)}</>
          ) : phase === 'idle' ? (
            'heats when started'
          ) : null}
        </span>
        <span className="flex-1" />
        <div className="flex items-center gap-2">
          {canStart && (
            <Button variant="primary" size="sm" disabled={busy} onClick={onStart}>
              Start
            </Button>
          )}
          {canAck && (
            <Button variant="primary" size="sm" disabled={busy} onClick={onAck}>
              Acknowledge
            </Button>
          )}
          {canFinish && (
            <Button variant={canAck ? 'secondary' : 'primary'} size="sm" disabled={busy} onClick={onFinish}>
              Done
            </Button>
          )}
          {canAbort && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onAbort} title="Cancel session (false-positive start)">
              Cancel
            </Button>
          )}
        </div>
      </div>

      {phase === 'warming' && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress != null ? Math.round(progress * 100) : undefined}
          aria-label="Warming progress"
          className="relative h-1.5 w-full overflow-hidden rounded-full bg-(--bg-surface-strong)"
        >
          <div
            className="h-full rounded-full bg-(--accent-warning) motion-safe:animate-[heatingShimmer_2.2s_linear_infinite]"
            style={{ width: progress != null ? `${Math.round(progress * 100)}%` : '12%' }}
          />
        </div>
      )}
    </div>
  );
}
